/**
 * RMA processing form for inbound returns — validates replacement radio,
 * warranty disposition, fees, shipping/billing contacts, and inventory
 * adjustments before posting to /rmas/:id/process/.
 *
 * Staff-only form; accessed from ReturnView when the RMA is in a
 * processable state (status 0 or 1). Owns form state, side-effects, and
 * submission; delegates each card to a section component under sections/.
 *
 * Contains:
 * - ProcessReturnForm        — the route-mounted form content (default export)
 * - buildInventoryAdjustments — assembles the inventory_adjustments payload
 */

import { Box, Button, Stack } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { Form, Formik } from 'formik';
import { List, Map } from 'immutable';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ImagePicker from '@sophocles/components/ImagePicker';
import { fetchKoreSubscriptionByICCID, postKoreProvision } from '@sophocles/entities/KoreSubscription/actions';
import { rawFetchRadioBillingForRadio } from '@sophocles/entities/RadioBilling';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import {
  useDeleteRmaImageMutation,
  usePatchRmaMutation,
  useProcessRmaMutation,
} from '@sophocles/features/entities/rmas';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';

import { SectionCard } from '../components/ReturnSection';
import { billContactFields, isValidContactId, shipContactFields } from '../utils/contactPayload';
import { buildContactFromRma } from '../utils/contactSeeding';
import { returnEntityLabels } from '../utils/tenantLabels';

import BillingSection from './sections/BillingSection';
import InstructionsReportingSection from './sections/InstructionsReportingSection';
import InventorySection from './sections/InventorySection';
import ProcessingDetailsSection from './sections/ProcessingDetailsSection';
import RmaDetailsSection from './sections/RmaDetailsSection';
import ShippingSection from './sections/ShippingSection';
import useRmaReferenceData from './useRmaReferenceData';
import { isValidShippingCost } from './validation';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Assemble the inventory_adjustments array for the POST payload.
 *
 * @param {import('immutable').Map} packingSupplies
 * @param {import('immutable').Map} removeFromInventory
 * @param {import('immutable').Map} addToInventory
 * @returns {Array}
 */
function buildInventoryAdjustments(packingSupplies, removeFromInventory, addToInventory) {
  const packing = packingSupplies
    .map((qty, id) => ({ quantity_shipped: qty, product_id: id, packing_supply: true }))
    .toList()
    .toArray();

  const removing = removeFromInventory
    .map((qty, id) => ({ quantity_shipped: qty, product_id: id, packing_supply: false }))
    .toList()
    .toArray();

  const adding = addToInventory
    .map((qty, id) => ({ quantity_added_to_inventory: qty, product_id: id, packing_supply: false }))
    .toList()
    .toArray();

  return [...packing, ...removing, ...adding];
}

/**
 * Format an ISO date string for display (e.g. "06/02/2026").
 * Returns null when the value is falsy.
 *
 * @param {string|null} isoString
 * @returns {string|null}
 */
function fmtDate(isoString) {
  if (!isoString) return null;
  try {
    return format(parseISO(isoString), 'M/d/yyyy');
  } catch {
    return isoString;
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Form content for processing an inbound RMA.
 *
 * The parent ReturnView owns chrome (AppNav / TitleBar); this component
 * renders only the form body. On successful POST to /rmas/:id/process/
 * it navigates to /rma/:id/view.
 *
 * @param {{ rma: import('immutable').Map }} props
 */
export default function ProcessReturnForm({ rma }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUserSelector);

  const [processRma] = useProcessRmaMutation();
  const [patchRma] = usePatchRmaMutation();
  const [deleteRmaImage] = useDeleteRmaImageMutation();

  useRmaReferenceData();

  // ── Derived read-only RMA values ──────────────────────────────────────────

  const rmaId = rma.get('id', null);
  const rmaStatus = rma.get('status', 1);

  const customer = rma ? rma.getIn(['site_group', 'customer'], Map()) : Map();
  const customerId = customer ? customer.get('id', 0) : 0;
  const entityLabels = returnEntityLabels(t, customer);
  const siteGroup = rma ? rma.get('site_group', Map()) : Map();
  const siteGroupId = siteGroup ? siteGroup.get('id', 0) : 0;

  const legacyReplacedIccid = rma.get('legacy_replaced_iccid', null);

  const oldICCID = rma ? rma.getIn(['radio_returned_by_user', 'iccid'], null) : null;
  const oldRadioId = rma ? rma.getIn(['radio_returned_by_user', 'id'], 0) : 0;

  const isQuickShip = rma ? rma.get('is_quick_ship', false) : false;

  const pastInventoryAdjustments = rma ? rma.get('inventory_adjustments', List()) : List();

  const initialTestResultsIdFromRma = rma ? rma.getIn(['initial_test_results', 'id'], null) : null;
  const warrantyFeeIdFromRma = rma ? rma.getIn(['warranty_fee', 'id'], null) : null;
  const warrantyStatusIdFromRma = rma ? rma.getIn(['warranty_status', 'id'], null) : null;

  const isStaff = user.get('is_staff', false);

  // ── Formik initialValues — scalar text fields ─────────────────────────────

  const initialValues = {
    comments: rma.get('comments', '') || '',
    hiddenComments: rma.get('hidden_comments', '') || '',
    trackingNumber: rma.get('tracking_number', '') || '',
    shippingCost: rma.get('shipping_cost', '') || '',
    unauthorizedSwap: rma.get('unauthorized_swap', false),
  };

  // ── External-component state (not in Formik values) ───────────────────────
  // Follows EditTicketForm pattern: these are controlled by sub-components
  // that don't bind to Formik Field, so they live in useState.

  const [loading, setLoading] = useState(false);

  const [issueCategory, setIssueCategory] = useState(rma.getIn(['issue', 'category', 'id'], 4));
  const [issueSubCategory, setIssueSubCategory] = useState(rma.getIn(['issue', 'id'], 2));

  const [newRadio, setNewRadio] = useState(rma.get('radio_shipped_to_user', null));
  const [noRadio, setNoRadio] = useState(false);

  const [selectedRmaFees, setSelectedRmaFees] = useState(rma.get('additional_fees', List()).map(v => v.get('id', 0)));

  const [packingSupplies, setPackingSupplies] = useState(Map());
  const [removeFromInventory, setRemoveFromInventory] = useState(Map());
  const [addToInventory, setAddToInventory] = useState(Map());

  const [shippingIncludedCheckbox, setShippingIncludedCheckbox] = useState(!!rma.get('shipping_cost_included_on_rma'));
  const [shippingCostError, setShippingCostError] = useState(null);

  const shippingCostIncludedOnRma = rma ? rma.get('shipping_cost_included_on_rma', null) : null;

  const [selectedShippingContact, setSelectedShippingContact] = useState(() => buildContactFromRma(rma, 'shipping'));
  const [selectedBillingContact, setSelectedBillingContact] = useState(() => buildContactFromRma(rma, 'billing'));
  const [selectedReportingContact, setSelectedReportingContact] = useState(() => buildContactFromRma(rma, 'reporting'));

  const [customerSpecialInstructionsFollowed, setCustomerSpecialInstructionsFollowed] = useState(true);
  const [siteGroupInstructionsFollowed, setSiteGroupInstructionsFollowed] = useState(true);

  const [picture, setPicture] = useState(rma.get('picture', null));
  const [pictureFile, setPictureFile] = useState(null);

  const [initialTestResultsId, setInitialTestResultsId] = useState(initialTestResultsIdFromRma);
  const [warrantyStatusId, setWarrantyStatusId] = useState(warrantyStatusIdFromRma);
  const [warrantyFeeId, setWarrantyFeeId] = useState(warrantyFeeIdFromRma);
  const [shippingCostIncludedOnRmaId, setShippingCostIncludedOnRmaId] = useState(
    shippingCostIncludedOnRma ? shippingCostIncludedOnRma.get('id', null) : null,
  );

  const [radioBilling, setRadioBilling] = useState(false);
  const [oldActivationState, setOldActivationState] = useState(Map());

  // ── Side-effects ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!oldRadioId) {
      setRadioBilling(null);
      return;
    }
    dispatch(rawFetchRadioBillingForRadio(oldRadioId)).then(res => setRadioBilling(res));
  }, [dispatch, oldRadioId]);

  useEffect(() => {
    if (!oldICCID) return;
    dispatch(fetchKoreSubscriptionByICCID(oldICCID)).then(res => {
      const s = res.subscriptions.get(oldICCID).state;
      setOldActivationState(s);
    });
  }, [dispatch, oldICCID]);

  // ── Derived display values ────────────────────────────────────────────────

  const warrantyStartDate =
    radioBilling && radioBilling.get('warranty_start_date', null)
      ? fmtDate(radioBilling.get('warranty_start_date'))
      : radioBilling && radioBilling.get('billing_start_date', null)
        ? fmtDate(radioBilling.get('billing_start_date'))
        : null;

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleIssueCategoryChange = useCallback((category, subCategory) => {
    setIssueCategory(category);
    setIssueSubCategory(subCategory);
  }, []);

  const handleRmaFeeSelection = useCallback(selected => setSelectedRmaFees(prev => prev.concat(selected)), []);

  const handleRmaFeeDeselect = useCallback(index => setSelectedRmaFees(prev => prev.delete(index)), []);

  const setRemoveFromInventoryValue = useCallback(
    (id, value) => setRemoveFromInventory(prev => prev.set(id + '', value)),
    [],
  );

  const setAddToInventoryValue = useCallback((id, value) => setAddToInventory(prev => prev.set(id + '', value)), []);

  const setPackingSuppliesValue = useCallback((id, value) => setPackingSupplies(prev => prev.set(id + '', value)), []);

  const deletePictureData = useCallback(() => {
    if (!rmaId) {
      dispatch(
        addToast({
          message: t('rma.imageRemoveNoRma', 'Could not remove image, RMA could not be found.'),
          level: 'error',
        }),
      );
      return;
    }
    deleteRmaImage({ id: rmaId })
      .unwrap()
      .then(() => {
        setPicture(null);
        dispatch(
          addToast({
            title: t('general.success', 'Success'),
            message: t('rma.rmaImageDeleted', 'RMA image deleted'),
            level: 'success',
            dismissible: true,
          }),
        );
      })
      .catch(() =>
        dispatch(addToast({ message: t('rma.imageRemoveError', 'Error removing picture'), level: 'error' })),
      );
  }, [deleteRmaImage, dispatch, t, rmaId]);

  // When the user clears a staged file that replaces an already-saved photo,
  // delete the saved photo from the server immediately.
  const handlePictureSelect = useCallback(
    selectedFile => {
      if (selectedFile === null && picture) {
        deletePictureData();
      }
      setPictureFile(selectedFile);
    },
    [deletePictureData, picture],
  );

  const handleActivateKore = useCallback(() => {
    if (!oldICCID) return;
    dispatch(postKoreProvision(oldICCID, 'reactivate')).then(() => {
      dispatch(fetchKoreSubscriptionByICCID(oldICCID)).then(res => {
        setOldActivationState(res.subscriptions.get(oldICCID).state);
      });
    });
  }, [dispatch, oldICCID]);

  const handleDeactivateKore = useCallback(() => {
    if (!oldICCID) return;
    dispatch(postKoreProvision(oldICCID, 'deactivate')).then(() => {
      dispatch(fetchKoreSubscriptionByICCID(oldICCID)).then(res => {
        setOldActivationState(res.subscriptions.get(oldICCID).state);
      });
    });
  }, [dispatch, oldICCID]);

  // ── Formik onSubmit ───────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    (values, { setSubmitting }) => {
      if (!rmaId) {
        setSubmitting(false);
        return;
      }

      if (!isValidShippingCost(values.shippingCost)) {
        dispatch(
          addToast({
            message: t('rma.shippingCostInvalid', 'Shipping cost must be a valid dollar amount (e.g. 12.50).'),
            level: 'error',
          }),
        );
        setShippingCostError(t('rma.mustBeValidDollar', 'Must be a valid dollar amount (e.g. 12.50)'));
        setSubmitting(false);
        return;
      }

      const payload = {
        issue_id: issueSubCategory,
        radio_shipped_to_user_id: newRadio ? newRadio.get('id', 0) : null,
        unauthorized_swap: values.unauthorizedSwap,
        comments: values.comments,
        hidden_comments: values.hiddenComments,
        tracking_number: values.trackingNumber,
        shipping_cost: values.shippingCost || null,
        inventory_adjustments: buildInventoryAdjustments(packingSupplies, removeFromInventory, addToInventory),
        additional_fees_ids: selectedRmaFees.toArray(),

        shipping_contact:
          selectedShippingContact?.id &&
          isValidContactId(selectedShippingContact.id) &&
          !selectedShippingContact?._isAddress
            ? selectedShippingContact.id
            : null,
        billing_contact:
          selectedBillingContact?.id &&
          isValidContactId(selectedBillingContact.id) &&
          !selectedBillingContact?._isAddress
            ? selectedBillingContact.id
            : null,
        support_contact:
          selectedReportingContact?.id &&
          isValidContactId(selectedReportingContact.id) &&
          !selectedReportingContact?._isAddress
            ? selectedReportingContact.id
            : null,
      };

      const contactNames = {
        siteGroupName: rma.getIn(['site_group', 'name'], ''),
        customerName: rma.getIn(['site_group', 'customer', 'name'], ''),
      };

      if (selectedShippingContact) {
        Object.assign(payload, shipContactFields(selectedShippingContact, contactNames));
      }

      if (selectedBillingContact) {
        Object.assign(payload, billContactFields(selectedBillingContact, contactNames));
      }

      if (selectedReportingContact) {
        payload.support_contact_name = selectedReportingContact.name || selectedReportingContact.business_name || '';
        payload.support_contact_email = selectedReportingContact.email || '';
        payload.support_contact_phone = selectedReportingContact.phone1 || '';
      }

      payload.initial_test_results_id = initialTestResultsId;
      payload.warranty_status_id = warrantyStatusId;
      payload.warranty_fee_id = warrantyFeeId;

      if (shippingIncludedCheckbox) {
        payload.shipping_cost_included_on_rma_id = shippingCostIncludedOnRmaId;
      }

      // Picture upload errors must NOT block navigation (legacy behavior): the
      // process write already succeeded, so swallow the picture failure.
      const uploadPictureIfStaged = () => {
        if (!pictureFile) return Promise.resolve();
        return patchRma({ id: rmaId, picture: pictureFile })
          .unwrap()
          .then(() =>
            dispatch(
              addToast({
                title: t('general.success', 'Success'),
                message: t('rma.rmaPictureUpdated', 'RMA picture updated'),
                level: 'success',
                dismissible: true,
              }),
            ),
          )
          .catch(() =>
            dispatch(
              addToast({
                title: t('general.error', 'Error'),
                message: t('rma.errorSavingPicture', 'Error saving picture'),
                level: 'error',
                dismissible: true,
              }),
            ),
          );
      };

      setLoading(true);
      processRma({ id: rmaId, body: payload })
        .unwrap()
        .then(() => {
          dispatch(
            addToast({
              title: t('general.success', 'Success'),
              message: t('rma.rmaProcessed', 'RMA processed'),
              level: 'success',
              dismissible: true,
            }),
          );
          uploadPictureIfStaged().then(() => {
            setLoading(false);
            setSubmitting(false);
            navigate(`/rma/${rmaId}/view`, { replace: true });
          });
        })
        .catch(err => {
          setLoading(false);
          setSubmitting(false);
          const { nonFieldMessages } = parseApiError(err, {
            knownFields: [],
            genericMessage: t('rma.processError', 'Error processing RMA'),
          });
          dispatch(
            addToast({
              message: nonFieldMessages[0] ?? t('rma.processError', 'Error processing RMA'),
              level: 'error',
            }),
          );
        });
    },
    [
      rmaId,
      rma,
      issueSubCategory,
      newRadio,
      shippingIncludedCheckbox,
      initialTestResultsId,
      warrantyStatusId,
      warrantyFeeId,
      shippingCostIncludedOnRmaId,
      packingSupplies,
      removeFromInventory,
      addToInventory,
      selectedRmaFees,
      selectedShippingContact,
      selectedBillingContact,
      selectedReportingContact,
      pictureFile,
      processRma,
      patchRma,
      dispatch,
      navigate,
      t,
    ],
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', overflow: 'auto', flex: 1 }}>
      <Box sx={{ width: '100%' }}>
        <RmaDetailsSection rma={rma} entityLabels={entityLabels} isStaff={isStaff} />

        <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
          {({ submitForm, isSubmitting }) => {
            const canSubmitForm =
              (newRadio !== null || noRadio || !!legacyReplacedIccid) &&
              customerSpecialInstructionsFollowed &&
              siteGroupInstructionsFollowed &&
              !loading &&
              !isSubmitting &&
              !shippingCostError;

            return (
              <Form>
                <InstructionsReportingSection
                  customerId={customerId}
                  siteGroupId={siteGroupId}
                  customer={customer}
                  siteGroup={siteGroup}
                  selectedReportingContact={selectedReportingContact}
                  onReportingContactChange={setSelectedReportingContact}
                  customerSpecialInstructionsFollowed={customerSpecialInstructionsFollowed}
                  onToggleCustomerInstructions={() => setCustomerSpecialInstructionsFollowed(prev => !prev)}
                  siteGroupInstructionsFollowed={siteGroupInstructionsFollowed}
                  onToggleSiteGroupInstructions={() => setSiteGroupInstructionsFollowed(prev => !prev)}
                />

                <ProcessingDetailsSection
                  rma={rma}
                  oldICCID={oldICCID}
                  oldActivationState={oldActivationState}
                  onActivateKore={handleActivateKore}
                  onDeactivateKore={handleDeactivateKore}
                  issueCategory={issueCategory}
                  issueSubCategory={issueSubCategory}
                  onIssueCategoryChange={handleIssueCategoryChange}
                  initialTestResultsId={initialTestResultsId}
                  onInitialTestResultsChange={setInitialTestResultsId}
                  warrantyStartDate={warrantyStartDate}
                  warrantyStatusId={warrantyStatusId}
                  onWarrantyStatusChange={setWarrantyStatusId}
                  warrantyFeeId={warrantyFeeId}
                  onWarrantyFeeChange={setWarrantyFeeId}
                  selectedRmaFees={selectedRmaFees}
                  onRmaFeeAdd={handleRmaFeeSelection}
                  onRmaFeeRemove={handleRmaFeeDeselect}
                  isQuickShip={isQuickShip}
                  newRadio={newRadio}
                  onNewRadioSelect={setNewRadio}
                  noRadio={noRadio}
                  onSetNoRadio={setNoRadio}
                />

                <ShippingSection
                  isQuickShip={isQuickShip}
                  selectedShippingContact={selectedShippingContact}
                  onShippingContactChange={setSelectedShippingContact}
                  customerId={customerId}
                  siteGroupId={siteGroupId}
                  customer={customer}
                  siteGroup={siteGroup}
                  shippingIncludedCheckbox={shippingIncludedCheckbox}
                  onToggleShippingIncluded={() => setShippingIncludedCheckbox(prev => !prev)}
                  shippingCostError={shippingCostError}
                  onShippingCostError={setShippingCostError}
                  shippingCostIncludedOnRmaId={shippingCostIncludedOnRmaId}
                  onShippingCostIncludedChange={setShippingCostIncludedOnRmaId}
                  shippingCostIncludedOnRma={shippingCostIncludedOnRma}
                />

                <BillingSection
                  isQuickShip={isQuickShip}
                  selectedBillingContact={selectedBillingContact}
                  onBillingContactChange={setSelectedBillingContact}
                  customerId={customerId}
                  siteGroupId={siteGroupId}
                  customer={customer}
                  siteGroup={siteGroup}
                />

                <InventorySection
                  pastInventoryAdjustments={pastInventoryAdjustments}
                  isQuickShip={isQuickShip}
                  removeFromInventory={removeFromInventory}
                  onRemoveFromInventoryChange={setRemoveFromInventoryValue}
                  addToInventory={addToInventory}
                  onAddToInventoryChange={setAddToInventoryValue}
                  packingSupplies={packingSupplies}
                  onPackingSuppliesChange={setPackingSuppliesValue}
                />

                {rmaId && rmaStatus !== 100 && (
                  <SectionCard title={t('rma.picture', 'RMA Picture')}>
                    <ImagePicker currentUrl={picture} file={pictureFile} onSelect={handlePictureSelect} />
                  </SectionCard>
                )}

                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ py: 2 }}>
                  <Button variant="outlined" color="inherit" onClick={() => navigate(-1)}>
                    {t('general.cancel', 'Cancel')}
                  </Button>
                  <Button variant="contained" color="primary" disabled={!canSubmitForm} onClick={submitForm}>
                    {t('general.submit', 'Submit')}
                  </Button>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Box>
  );
}
